import { type OrganizationView } from "@nabble/sdk";
import { Badge, Card, Field, fmt, Select, useToast } from "@nabble/ui";
import { useState } from "react";
import type { AccountInfo } from "../../../../api/account";
import { useAuth } from "../../../../auth/AuthContext";
import { errorMessage } from "../../../../errors";
import { setOrganizationView } from "../../../../api/me";
import { roleNames } from "../../../../model/organizationRoles";
import { reloadInModule } from "../../../../model/reloadInModule";
import { EmailsSection } from "../emails/EmailsSection";

/** My account → Profile: who you are, your emails and, with several organizations, how to see them. */
export function ProfileSection({ account, onChanged }: { account: AccountInfo; onChanged: () => void }): React.JSX.Element {
  const { user } = useAuth();
  return (
    <>
      <Card id="profile" className="nb-setting" padded={false}>
        <div className="nb-setting-row">
          <div>
            <div className="nb-setting-title">Profile</div>
            <p className="nb-setting-desc">To change your name, ask an administrator.</p>
          </div>
          <div className="nb-setting-content">
            <div className="nb-cell-main">
              <span className="nb-avatar nb-avatar-lg">{fmt.initials(account.displayName)}</span>
              <div className="nb-cell-stack">
                <strong style={{ fontSize: 16, lineHeight: "22px" }}>{account.displayName}</strong>
                <span className="nb-subtle nb-small">{account.email}</span>
              </div>
            </div>
            <dl className="nb-kv" style={{ marginTop: "var(--space-2)" }}>
              <div>
                <dt>organizations</dt>
                <dd>
                  {user && user.organizations.length > 0
                    ? user.organizations.map((o) => `${o.name} (${roleNames(o.roles)})`).join(", ")
                    : "None yet"}
                </dd>
              </div>
              {user?.suiteAdmin ? (
                <div>
                  <dt>suite</dt>
                  <dd>Suite administrator</dd>
                </div>
              ) : null}
              <div>
                <dt>second factor</dt>
                <dd>
                  {account.totp || account.passkeys.length > 0 ? <Badge label="Set up" tone="success" /> : <Badge label="Pending" tone="warning" />}
                </dd>
              </div>
              <div>
                <dt>open sessions</dt>
                <dd className="nb-num">{fmt.number(account.sessions.length)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </Card>

      <Card id="emails" className="nb-setting" padded={false}>
        <EmailsSection account={account} onChanged={onChanged} />
      </Card>

      {user && user.organizations.length > 1 ? <OrganizationViewSection /> : null}
    </>
  );
}

/** One organization at a time, or all of them together so there's no need to keep switching. */
function OrganizationViewSection(): React.JSX.Element {
  const { user } = useAuth();
  const { show } = useToast();
  const [saving, setSaving] = useState(false);
  const view: OrganizationView = user?.organizationView ?? "SINGLE";

  async function change(next: OrganizationView): Promise<void> {
    if (next === view) return;
    setSaving(true);
    try {
      await setOrganizationView(next);
      reloadInModule(window.location.pathname);
    } catch (err) {
      show({ title: "We couldn't save your choice", text: errorMessage(err, "Please try again."), tone: "danger" });
      setSaving(false);
    }
  }

  return (
    <Card id="organizations" className="nb-setting" padded={false}>
      <div className="nb-setting-row">
        <div>
          <div className="nb-setting-title">Your organizations</div>
          <p className="nb-setting-desc">
            Work in one organization at a time, or see the work of all of them together. You can also switch from the bottom of the menu.
          </p>
        </div>
        <div className="nb-setting-content">
          <Field label="Show">
            <Select
              value={view}
              disabled={saving}
              onValueChange={(v) => void change(v as OrganizationView)}
              options={[
                { value: "SINGLE", label: "One organization at a time", hint: user?.organization?.name },
                { value: "COMBINED", label: "All my organizations together" },
              ]}
            />
          </Field>
        </div>
      </div>
    </Card>
  );
}
